import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import { Checkbox } from '../../../components/ui/Checkbox';
import Select from '../../../components/ui/Select';
import Icon from '../../../components/AppIcon';
import { useAuth } from '../../../contexts/AuthContext';

const RegistrationForm = ({ onRegistrationSuccess }) => {
  const navigate = useNavigate();
  const { signUp } = useAuth();

  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    password: '',
    confirmPassword: '',
    postcode: '',
    city: '',
    accountType: 'client',
    agreeToTerms: false,
    agreeToPrivacy: false,
    emailUpdates: false
  });

  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [passwordStrength, setPasswordStrength] = useState(0);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [registrationStep, setRegistrationStep] = useState('registration');

  const accountTypeOptions = [
    { value: 'client', label: 'Client - Looking for holistic services' },
    { value: 'practitioner', label: 'Practitioner - Offering holistic services' }
  ];

  const validatePostcode = (postcode) => {
    const ukPostcodeRegex = /^[A-Z]{1,2}[0-9][A-Z0-9]? ?[0-9][A-Z]{2}$/i;
    return ukPostcodeRegex?.test(postcode?.replace(/\s/g, ''));
  };

  const calculatePasswordStrength = (password) => {
    let strength = 0;
    if (password?.length >= 8) strength++;
    if (/[A-Z]/?.test(password)) strength++;
    if (/[a-z]/?.test(password)) strength++;
    if (/[0-9]/?.test(password)) strength++;
    if (/[^A-Za-z0-9]/?.test(password)) strength++;
    return strength;
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e?.target;
    const newValue = type === 'checkbox' ? checked : value;

    setFormData(prev => ({
      ...prev,
      [name]: newValue
    }));

    if (name === 'password') {
      setPasswordStrength(calculatePasswordStrength(value));
    }

    // Clear errors when user starts typing
    if (errors?.[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    // Name validation
    if (!formData?.firstName?.trim()) {
      newErrors.firstName = 'First name is required';
    }
    if (!formData?.lastName?.trim()) {
      newErrors.lastName = 'Last name is required';
    }

    // Email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!formData?.email) {
      newErrors.email = 'Email is required';
    } else if (!emailRegex?.test(formData?.email)) {
      newErrors.email = 'Please enter a valid email address';
    }

    // Password validation
    if (!formData?.password) {
      newErrors.password = 'Password is required';
    } else if (formData?.password?.length < 8) {
      newErrors.password = 'Password must be at least 8 characters long';
    }

    // Confirm password validation
    if (!formData?.confirmPassword) {
      newErrors.confirmPassword = 'Please confirm your password';
    } else if (formData?.password !== formData?.confirmPassword) {
      newErrors.confirmPassword = 'Passwords do not match';
    }

    // Postcode validation
    if (!formData?.postcode) {
      newErrors.postcode = 'Postcode is required';
    } else if (!validatePostcode(formData?.postcode)) {
      newErrors.postcode = 'Please enter a valid UK postcode';
    }

    // City validation (optional but if provided, should not be empty)
    if (formData?.city && !formData?.city?.trim()) {
      newErrors.city = 'Please enter a valid city name';
    }

    // Terms and privacy validation
    if (!formData?.agreeToTerms) {
      newErrors.agreeToTerms = 'You must agree to the terms and conditions';
    }
    if (!formData?.agreeToPrivacy) {
      newErrors.agreeToPrivacy = 'You must agree to the privacy policy';
    }

    setErrors(newErrors);
    return Object.keys(newErrors)?.length === 0;
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();

    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);

    try {
      // Enhanced metadata preparation with improved location storage
      const registrationMetadata = {
        fullName: `${formData?.firstName?.trim()} ${formData?.lastName?.trim()}`,
        role: formData?.accountType,
        location: formData?.city ? `${formData?.city?.trim()}, ${formData?.postcode?.trim()}` : formData?.postcode?.trim(),
        postcode: formData?.postcode?.trim(),
        city: formData?.city?.trim(),
        emailUpdates: formData?.emailUpdates,
        // Add additional metadata for comprehensive profile creation
        firstName: formData?.firstName?.trim(),
        lastName: formData?.lastName?.trim(),
        preferences: {
          marketing: formData?.emailUpdates,
          notifications: true
        }
      };

      const { data, error } = await signUp(
        formData?.email,
        formData?.password,
        registrationMetadata
      );

      if (error) {
        // Handle specific Supabase errors more gracefully
        let errorMessage = 'Registration failed. Please try again.';

        if (error?.includes('email')) {
          errorMessage = 'This email address is already registered. Please try logging in instead.';
        } else if (error?.includes('password')) {
          errorMessage = 'Password does not meet requirements. Please try a different password.';
        } else if (error?.includes('already exists') || error?.includes('already registered')) {
          errorMessage = 'An account with this email already exists. Please try signing in instead.';
        } else if (error?.includes('server error') || error?.includes('Database error')) {
          errorMessage = 'Registration failed due to a server error. Please try again in a moment.';
        } else if (error?.includes('internet') || error?.includes('connection')) {
          errorMessage = 'Please check your internet connection and try again.';
        }

        setErrors({ submit: errorMessage });
      } else if (data?.user) {
        // Store registration data for verification step
        localStorage?.setItem('registrationData', JSON.stringify({
          email: formData?.email,
          accountType: formData?.accountType,
          fullName: `${formData?.firstName?.trim()} ${formData?.lastName?.trim()}`
        }));

        // Enhanced success handling
        if (data?.user?.email_confirmed_at || data?.session) {
          // User is immediately confirmed, navigate to profile
          console.log('Registration successful, user confirmed immediately');
          navigate('/user-profile', { replace: true });
        } else {
          // User needs email verification
          console.log('Registration successful, awaiting email verification');
          if (onRegistrationSuccess) {
            onRegistrationSuccess();
          } else {
            // Navigate to verification step within the same page
            setRegistrationStep('verification');
          }
        }
      }
    } catch (error) {
      console.error('Registration submission error:', error);
      setErrors({
        submit: 'Registration failed. Please check your connection and try again.'
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const getPasswordStrengthColor = () => {
    if (passwordStrength <= 1) return 'bg-destructive';
    if (passwordStrength <= 3) return 'bg-warning';
    return 'bg-success';
  };

  const getPasswordStrengthText = () => {
    if (passwordStrength <= 1) return 'Weak';
    if (passwordStrength <= 3) return 'Medium';
    return 'Strong';
  };

  return (
    <div className="w-full max-w-md mx-auto">
      <div className="text-center mb-8">
        <h1 className="text-3xl font-heading font-semibold text-foreground mb-2">
          Join Our Community
        </h1>
        <p className="text-muted-foreground font-body">
          Create your account to save listings, book sessions, and connect with practitioners
        </p>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Account Type Selection */}
        <Select
          label="Account Type"
          name="accountType"
          value={formData?.accountType}
          onChange={handleInputChange}
          options={accountTypeOptions}
          error={errors?.accountType}
          required
          disabled={isSubmitting}
        />

        {/* Name Fields */}
        <div className="grid grid-cols-2 gap-4">
          <Input
            label="First Name"
            type="text"
            name="firstName"
            value={formData?.firstName}
            onChange={handleInputChange}
            placeholder="Enter first name"
            error={errors?.firstName}
            required
            disabled={isSubmitting}
          />
          <Input
            label="Last Name"
            type="text"
            name="lastName"
            value={formData?.lastName}
            onChange={handleInputChange}
            placeholder="Enter last name"
            error={errors?.lastName}
            required
            disabled={isSubmitting}
          />
        </div>

        {/* Email */}
        <Input
          label="Email Address"
          type="email"
          name="email"
          value={formData?.email}
          onChange={handleInputChange}
          placeholder="Enter your email"
          description="We'll send a verification email to this address"
          error={errors?.email}
          required
          disabled={isSubmitting}
        />

        {/* Password */}
        <div className="space-y-2">
          <div className="relative">
            <Input
              label="Password"
              type={showPassword ? "text" : "password"}
              name="password"
              value={formData?.password}
              onChange={handleInputChange}
              placeholder="Create a strong password"
              error={errors?.password}
              required
              disabled={isSubmitting}
            />
            <button
              type="button"
              onClick={() => setShowPassword(!showPassword)}
              className="absolute right-3 top-9 text-muted-foreground hover:text-foreground transition-gentle"
              disabled={isSubmitting}
            >
              <Icon name={showPassword ? "EyeOff" : "Eye"} size={16} />
            </button>
          </div>

          {formData?.password && (
            <div className="space-y-1">
              <div className="flex items-center justify-between text-xs">
                <span className="text-muted-foreground">Password strength:</span>
                <span className={`font-medium ${
                  passwordStrength <= 1 ? 'text-destructive' :
                  passwordStrength <= 3 ? 'text-warning' : 'text-success'
                }`}>
                  {getPasswordStrengthText()}
                </span>
              </div>
              <div className="w-full bg-muted rounded-full h-1">
                <div
                  className={`h-1 rounded-full transition-all ${getPasswordStrengthColor()}`}
                  style={{ width: `${(passwordStrength / 5) * 100}%` }}
                />
              </div>
            </div>
          )}
        </div>

        {/* Confirm Password */}
        <div className="relative">
          <Input
            label="Confirm Password"
            type={showConfirmPassword ? "text" : "password"}
            name="confirmPassword"
            value={formData?.confirmPassword}
            onChange={handleInputChange}
            placeholder="Confirm your password"
            error={errors?.confirmPassword}
            required
            disabled={isSubmitting}
          />
          <button
            type="button"
            onClick={() => setShowConfirmPassword(!showConfirmPassword)}
            className="absolute right-3 top-9 text-muted-foreground hover:text-foreground transition-gentle"
            disabled={isSubmitting}
          >
            <Icon name={showConfirmPassword ? "EyeOff" : "Eye"} size={16} />
          </button>
        </div>

        {/* Location */}
        <div className="grid grid-cols-2 gap-4">
          <Input
            label="Postcode"
            type="text"
            name="postcode"
            value={formData?.postcode}
            onChange={handleInputChange}
            placeholder="SW1A 1AA"
            description="For local listings"
            error={errors?.postcode}
            required
            disabled={isSubmitting}
          />
          <Input
            label="City (Optional)"
            type="text"
            name="city"
            value={formData?.city}
            onChange={handleInputChange}
            placeholder="London"
            error={errors?.city}
            disabled={isSubmitting}
          />
        </div>

        {/* Enhanced Terms and Privacy with proper links */}
        <div className="space-y-4">
          <div className="flex items-start space-x-3">
            <Checkbox
              name="agreeToTerms"
              checked={formData?.agreeToTerms}
              onChange={handleInputChange}
              error={errors?.agreeToTerms}
              required
              disabled={isSubmitting}
            />
            <div className="flex-1 pt-1">
              <label className="text-sm font-body text-foreground">
                I agree to the{' '}
                <button
                  type="button"
                  onClick={() => window.open('/terms-conditions', '_blank')}
                  className="text-primary hover:text-primary/80 underline font-medium transition-gentle"
                  disabled={isSubmitting}
                >
                  Terms and Conditions
                </button>
              </label>
              {errors?.agreeToTerms && (
                <p className="mt-1 text-xs text-destructive">{errors?.agreeToTerms}</p>
              )}
            </div>
          </div>

          <div className="flex items-start space-x-3">
            <Checkbox
              name="agreeToPrivacy"
              checked={formData?.agreeToPrivacy}
              onChange={handleInputChange}
              error={errors?.agreeToPrivacy}
              required
              disabled={isSubmitting}
            />
            <div className="flex-1 pt-1">
              <label className="text-sm font-body text-foreground">
                I agree to the{' '}
                <button
                  type="button"
                  onClick={() => window.open('/privacy-policy', '_blank')}
                  className="text-primary hover:text-primary/80 underline font-medium transition-gentle"
                  disabled={isSubmitting}
                >
                  Privacy Policy
                </button>
              </label>
              {errors?.agreeToPrivacy && (
                <p className="mt-1 text-xs text-destructive">{errors?.agreeToPrivacy}</p>
              )}
            </div>
          </div>

          <div className="flex items-start space-x-3">
            <Checkbox
              name="emailUpdates"
              checked={formData?.emailUpdates}
              onChange={handleInputChange}
              disabled={isSubmitting}
            />
            <div className="flex-1 pt-1">
              <label className="text-sm font-body text-foreground">
                I'd like to receive email updates about new listings and features
              </label>
              <p className="text-xs text-muted-foreground mt-1">
                You can unsubscribe at any time in your account settings
              </p>
            </div>
          </div>
        </div>

        {/* Submit Error */}
        {errors?.submit && (
          <div className="p-4 bg-destructive/10 border border-destructive/20 rounded-lg">
            <div className="flex items-center space-x-2">
              <Icon name="AlertCircle" size={16} className="text-destructive" />
              <p className="text-sm text-destructive font-body">{errors?.submit}</p>
            </div>
          </div>
        )}

        {/* Submit Button */}
        <Button
          type="submit"
          variant="default"
          fullWidth
          loading={isSubmitting}
          disabled={isSubmitting}
          iconName="UserPlus"
          iconPosition="left"
        >
          {isSubmitting ? 'Creating Account...' : 'Create Account'}
        </Button>

        {/* Login Link */}
        <div className="text-center pt-4">
          <p className="text-sm text-muted-foreground font-body">
            Already have an account?{' '}
            <button
              type="button"
              onClick={() => navigate('/login')}
              className="text-primary hover:text-primary/80 font-medium transition-gentle"
              disabled={isSubmitting}
            >
              Sign in here
            </button>
          </p>
        </div>
      </form>
    </div>
  );
};

export default RegistrationForm;